import { useEffect, useState } from "react";
import { Occurrence, OccurrenceDispatch, OccurrenceReview, OccurrenceStep } from "../../../models/occurrence";
import {
  OccurrenceStepEnum,
  OccurrenceStepLabelEnum,
} from "../../../enums/occurrenceStep";
import { requiredFieldsByStep } from "../components/form/utils/required-fields-by-step";

import CallForm from "../components/form/callForm";
import ScreeningForm from "../components/form/screeningForm";
import DisplacementForm from "../components/form/displacementForm";
import SecondDiagnosisForm from "../components/form/secondDiagnosisForm";
import RemovalForm from "../components/form/removalForm";
import ThirdDiagnosisForm from "../components/form/thirdDiagnosisForm";
import FourthDiagnosisForm from "../components/form/fourthDiagnosisForm";
import AnalysisWarrantyForm from "../components/form/analysisWarrantyForm";
import CustomerOkForm from "../components/form/customerOkForm";
import PartsForm from "../components/form/partsForm";
import PartsInTransitForm from "../components/form/partsInTransitForm";
import AwaitingRepairForm from "../components/form/awaitingRepairForm";
import RepairForm from "../components/form/repairForm";
import ReleaseForm from "../components/form/releaseForm";
import ChangeStepForm from "../components/form/changeStepForm";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import AssessmentForm from "../components/form/assessmentForm";
import Worklog from "../components/worklog";
import { useSelector } from "react-redux";
import { getUserProfile } from "../../../configuration/login/loginSlice";

import { Scheduling } from "../../../models/schedule";

import { fetchSchedulingByChassisList } from "../../../api/fetchScheduling";
import { fetchUser, IUserProfile } from "../../../api/fetchUser";

import "../styles.scss";

interface IFormDialogProps {
  showDialog: boolean;
  onClose: () => void;
  occurrence: Occurrence | null;
  partners?: { id: string, name: string }[];
  summaryStep?: OccurrenceStep | null;
  step: OccurrenceStepEnum;
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  loading: boolean;
  save: (occurrence: Occurrence) => Promise<boolean>;
  disabledAllFields?: boolean;
  changeStep: (
    service: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null>;
  view: "form" | "changeStep" | "comments" | "worklog";
  setView: (view: "form" | "changeStep" | "comments" | "worklog") => void;
  getNotFilledRequiredFields: (
    requiredFields: string[],
    occurrence: Occurrence
  ) => string[];
  finishOccurrence: (occurrence: Occurrence, finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepId?: number) => Promise<boolean>;
  persona: "MANAGER" | "TOWER" | "CONSULTANT" | "PARTNERSHIP";
  finalized?: boolean;
  review: OccurrenceReview[];
  reviewStep: (occurrenceId: string, step: OccurrenceStepEnum, rate: number, comment?: string) => Promise<void>;
  occurrenceStepAvailable: string[];
  addDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
  cancelDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string) => Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
}

const StepFormDialog = (props: IFormDialogProps) => {
  const [occurrenceInit, setOccurrenceInit] = useState<Occurrence | null>(null);
  const [occurrenceFormHasChanged, setOccurrenceFormHasChanged] = useState<boolean>(false);
  const [occurrence, setOccurrence] = useState<Occurrence | null>(null);
  const [scheduling, setScheduling] = useState<Scheduling | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const userProfile = useSelector(getUserProfile);
  const [user, setUser] = useState<IUserProfile | undefined>(undefined);

  useEffect(() => {
    const fetchStep = async () => {
      const occurrence = props.occurrence;
      
      setOccurrence(occurrence ? { ...occurrence, accountName: props.occurrence?.accountName } : ({} as Occurrence));
      setOccurrenceInit(occurrence ? { ...occurrence, accountName: props.occurrence?.accountName } : ({} as Occurrence));

      if (occurrence?.scheduleNumber) {
        const scheduling = (await fetchSchedulingByChassisList([occurrence.vehicleChassis])).find(e => e.scheduleNumber === occurrence.scheduleNumber);
        setScheduling(scheduling);
      }

      const userId = userProfile?.sub?.split(":")[1];
      if (userId) setUser(await fetchUser(userId));
    };
    if (props.showDialog) fetchStep();
    else setOccurrenceFormHasChanged(false);
  }, [props.step, props.showDialog]);

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const formProps = {
    occurrence: occurrence,
    summaryStep: props.summaryStep,
    partners: props.partners,
    setOccurrence: (updatedOccurrence: Partial<Occurrence>) => {
      setOccurrenceFormHasChanged(true);
      setOccurrence({ ...occurrence, ...updatedOccurrence } as Occurrence);
    },
    setOccurrenceValue: (attr: string, value: any) => {
      setOccurrenceFormHasChanged(true);
      setOccurrence(prev => ({ ...prev, [attr]: value } as Occurrence));
    },
    setOccurrenceStepValue: (
      step: OccurrenceStepLabelEnum,
      attr: string,
      value: any
    ) => {
      setOccurrenceFormHasChanged(true);
      setOccurrence({
        ...occurrence,
        steps:
          occurrence?.steps?.map((stepEl) =>
            stepEl.name === step ? { ...stepEl, [attr]: value } : stepEl
          ) ?? [],
      } as Occurrence);
    },
    step:
      occurrence?.steps?.find((s) => s.step === props.step) ??
      ({} as OccurrenceStep),
    isValidStep: (requiredFields: string[]) =>
      props.isValidStep(requiredFields, occurrence ?? ({} as Occurrence)),
    loading,
    save: async (occurrence: Occurrence) => {
      setLoading(true);
      const result = await props.save(occurrence);
      if (result) {
        setOccurrenceInit(occurrence);
        setOccurrence({
          ...occurrence,
          steps: occurrence.steps?.map(step => {
            if (step.step === props.step) {
              return {
                ...step,
                updatedAt: new Date(),
                updatedBy: user?.firstName + " " + user?.lastName,
              }
            }
            return step;
          }),
        });
        setOccurrenceFormHasChanged(false);
      };
      setLoading(false);
      return result;
    },
    onClose: props.onClose,
    disabledAllFields: props.disabledAllFields,
    persona: props.persona,
    finalized: props.finalized,
    addDispatch: props.addDispatch,
    cancelDispatch: props.cancelDispatch,
    getNotFilledRequiredFields: props.getNotFilledRequiredFields,
    finishOccurrence: props.finishOccurrence,
    changeStep: props.changeStep,
    scheduling: scheduling,
  };

  const form = () => {
    switch (props.step) {
      case OccurrenceStepEnum.CALL:
        return <CallForm {...formProps} />;

      case OccurrenceStepEnum.SCREENING:
        return <ScreeningForm {...formProps} />;

      case OccurrenceStepEnum.DISPLACEMENT:
        return <DisplacementForm {...formProps} />;

      case OccurrenceStepEnum.SECOND_DIAGNOSIS:
        return <SecondDiagnosisForm {...formProps} />;

      case OccurrenceStepEnum.REMOVAL:
        return <RemovalForm {...formProps} />;

      case OccurrenceStepEnum.THIRD_DIAGNOSIS:
        return <ThirdDiagnosisForm {...formProps} />;

      case OccurrenceStepEnum.FOURTH_DIAGNOSIS:
        return <FourthDiagnosisForm {...formProps} />;

      case OccurrenceStepEnum.ANALYSIS_WARRANTY:
        return <AnalysisWarrantyForm {...formProps} />;

      case OccurrenceStepEnum.CUSTOMER_OK:
        return <CustomerOkForm {...formProps} />;

      case OccurrenceStepEnum.PARTS:
        return <PartsForm {...formProps} />;

      case OccurrenceStepEnum.PARTS_IN_TRANSIT:
        return <PartsInTransitForm {...formProps} />;

      case OccurrenceStepEnum.AWAITING_REPAIR:
        return <AwaitingRepairForm {...formProps} />;

      case OccurrenceStepEnum.REPAIR:
        return <RepairForm {...formProps} />;

      case OccurrenceStepEnum.RELEASE:
        return <ReleaseForm {...formProps} />;

      default:
        return null;
    }
  };

  const changeStepProps = {
    step:
      occurrence?.steps?.find((s) => s.step === props.step) ??
      ({} as OccurrenceStep),
    occurrence: occurrence ?? null,
    changeStep: async (
      occurrence: Occurrence,
      fromStepId: number,
      to: OccurrenceStepEnum
    ) => {
      const result = await props.changeStep(occurrence, fromStepId, to);
      if (!result) return null;
      setOccurrence(result);
      return result;
    },
    onClose: props.onClose,
    requiredFields: [] as string[],
    isValidStep: (requiredFields: string[]) =>
      props.isValidStep(requiredFields, occurrence ?? ({} as Occurrence)),
    hasUnsavedChanges: () => occurrenceFormHasChanged,
    getNotFilledRequiredFields: (
      requiredFields: string[],
      occurrence: Occurrence
    ) => props.getNotFilledRequiredFields(requiredFields, occurrence),
    finishOccurrence: async (occurrence: Occurrence, finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepId?: number) => {
      const result = await props.finishOccurrence(occurrence, finalizationReasonType, finalizationReasonDescription, fromStepId);
      return result;
    },
    occurrenceStepAvailable: props.occurrenceStepAvailable,
  };

  const changeStep = () => {
    let props = changeStepProps;
    switch (props.step.step) {
      case OccurrenceStepEnum.CALL:
        props.requiredFields = requiredFieldsByStep.CALL;
        break;

      case OccurrenceStepEnum.SCREENING:
        props.requiredFields = requiredFieldsByStep.SCREENING;
        break;

      case OccurrenceStepEnum.DISPLACEMENT:
        props.requiredFields = requiredFieldsByStep.DISPLACEMENT;
        break;

      case OccurrenceStepEnum.SECOND_DIAGNOSIS:
        props.requiredFields = requiredFieldsByStep.SECOND_DIAGNOSIS;
        break;

      case OccurrenceStepEnum.REMOVAL:
        props.requiredFields = requiredFieldsByStep.REMOVAL;
        break;

      case OccurrenceStepEnum.THIRD_DIAGNOSIS:
        props.requiredFields = requiredFieldsByStep.THIRD_DIAGNOSIS;
        break;

      case OccurrenceStepEnum.FOURTH_DIAGNOSIS:
        props.requiredFields = requiredFieldsByStep.FOURTH_DIAGNOSIS;
        break;

      case OccurrenceStepEnum.ANALYSIS_WARRANTY:
        props.requiredFields = requiredFieldsByStep.ANALYSIS_WARRANTY;
        break;

      case OccurrenceStepEnum.CUSTOMER_OK:
        props.requiredFields = requiredFieldsByStep.CUSTOMER_OK;
        break;

      case OccurrenceStepEnum.PARTS:
        props.requiredFields = requiredFieldsByStep.PARTS;
        break;

      case OccurrenceStepEnum.PARTS_IN_TRANSIT:
        props.requiredFields = requiredFieldsByStep.PARTS_IN_TRANSIT;
        break;

      case OccurrenceStepEnum.AWAITING_REPAIR:
        props.requiredFields = requiredFieldsByStep.AWAITING_REPAIR;
        break;

      case OccurrenceStepEnum.REPAIR:
        props.requiredFields = requiredFieldsByStep.REPAIR;
        break;

      case OccurrenceStepEnum.RELEASE:
        props.requiredFields = requiredFieldsByStep.RELEASE;
        break;

      default:
        props.requiredFields = [];
        break;
    }

    return <ChangeStepForm {...props} />;
  };

  const onCloseValidation = () => {
    if (occurrenceFormHasChanged) {
      setShowConfirmDialog(true);
      return;
    } else {
      props.onClose();
    }
  };

  return (
    props.showDialog && (
      <>
        <div
          className="bg-kanban-sidebar"
          onClick={() => onCloseValidation()}
        ></div>
        <div className="kanban-sidebar">
          <div className="flex justify-between items-center px-6">
            <div className="text-size-h4 p-4">Dados da ocorrência</div>

            <div className="p-4 text-size-h5 padding-right-10 cursor-pointer">
              <span
                className="rioglyph rioglyph-remove"
                onClick={() => onCloseValidation()}
              ></span>
            </div>
          </div>
          <hr className="border-color-gray-light border-bottom-solid !mb-0" />
          <div className="flex gap-3">
            <div className="w-[100px] text-center bg-lightest pt-6">
              {!props.summaryStep?.dtEnd &&
                props.persona !== "MANAGER" &&
                !props.finalized && (
                  <div className="p-2 mb-4">
                    <span
                      className={`rioglyph rioglyph-compare text-size-h1 cursor-pointer ${props.view === "changeStep"
                          ? "text-color-primary"
                          : "text-color-gray"
                        }`}
                      onClick={() => props.setView("changeStep")}
                    />
                  </div>
                )}
              <div className="p-2 mb-4">
                <span
                  className={`rioglyph rioglyph-clipboard text-size-h1 cursor-pointer ${props.view === "form"
                      ? "text-color-primary"
                      : "text-color-gray"
                    }`}
                  onClick={() => props.setView("form")}
                />
              </div>
              <div className="p-2 mb-4">
                <span
                  className={`rioglyph rioglyph-time text-size-h1 cursor-pointer ${props.view === "worklog"
                      ? "text-color-primary"
                      : "text-color-gray"
                    }`}
                  onClick={() => props.setView("worklog")}
                />
              </div>

              {props.persona === "MANAGER" && (props.occurrence?.steps?.find(step => step.step === props.step)?.dtEnd) && <div className="p-2 mb-4">
                <span
                  className={`rioglyph rioglyph-comment text-size-h1 cursor-pointer ${props.view === "comments"
                      ? "text-color-primary"
                      : "text-color-gray"
                    }`}
                  onClick={() => props.setView("comments")}
                />
              </div>}
            </div>
            <div className="w-full py-6 px-6 overflow-y-auto h-[calc(100vh-50px)] !pb-[50px]">
              {occurrence ? (
                props.view === "form" ? (
                  form()
                ) : props.view === "changeStep" ? (
                  changeStep()
                ) : props.view === "comments" ? (
                  <AssessmentForm
                    occurrenceId={occurrence.id}
                    defaultStep={props.step}
                    review={props.review}
                    reviewStep={props.reviewStep}
                    steps={occurrence.steps ?? []}
                  />
                ) : props.view === "worklog" ? (
                  <Worklog occurrenceId={occurrence.id} />
                ) : null
              ) : (
                <div className="flex items-center justify-center text-center h-full">
                  <div className="flex">
                    <div className="spinner mr-4">
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                      <div />
                    </div>
                    Carregando dados da ocorrência...
                  </div>
                </div>
              )}
            </div>
          </div>

          <ConfirmDialog
            show={showConfirmDialog}
            title="Deseja realmente sair?"
            body="Todas as alterações serão perdidas."
            onClose={() => setShowConfirmDialog(false)}
            onConfirm={() => {
              setOccurrence(
                occurrenceInit ? { ...occurrenceInit } : ({} as Occurrence)
              );
              props.onClose();
              setShowConfirmDialog(false);
            }}
          />
        </div>
      </>
    )
  );
};

export default StepFormDialog;
